import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Building2 } from 'lucide-react';

import { updateProfile } from '@/api/auth.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Self-service profile page. Phase 16 (multi-org session + self-profile).
 *
 * Anyone signed in can edit their own first/last name. Email is the sign-in
 * identity (Stytch-owned) and is shown read-only. The memberships card lists
 * every org this account belongs to — switching happens via the OrgSwitcher
 * in the header, not here.
 */
export function ProfileSettingsPage() {
  const { state, refresh } = useAuth();
  const toast = useToast();

  const me = state.status === 'authenticated' ? state.me : null;

  const [firstName, setFirstName] = useState(me?.user.firstName ?? '');
  const [lastName, setLastName] = useState(me?.user.lastName ?? '');

  const mutation = useMutation({
    mutationFn: () => updateProfile({ firstName: firstName.trim(), lastName: lastName.trim() }),
    onSuccess: async () => {
      toast.show('Profile updated', { variant: 'success' });
      // Re-hydrate the auth context so the header initials/name follow suit.
      await refresh();
    },
  });

  if (!me) return null;

  // Older cached /me responses may miss `memberships` — fall back to the
  // active membership so the card never renders empty.
  const memberships = me.memberships ?? [
    {
      id: me.membership.id,
      role: me.membership.role,
      organizationId: me.membership.organizationId,
      organization: me.organization,
    },
  ];

  const valid = firstName.trim().length > 0 && lastName.trim().length > 0;
  const dirty = firstName.trim() !== me.user.firstName || lastName.trim() !== me.user.lastName;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!valid || mutation.isPending) return;
    mutation.mutate();
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="typo-h1">Profile settings</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          Your name as it appears across the portal, and the organizations your account belongs to.
        </p>
      </div>

      <Card className="p-6">
        <form
          onSubmit={(e) => {
            handleSubmit(e);
          }}
          className="space-y-4"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              name="firstName"
              required
              maxLength={60}
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
              }}
            />
            <Input
              label="Last name"
              name="lastName"
              required
              maxLength={60}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
              }}
            />
          </div>
          <Input
            label="Email"
            name="email"
            value={me.user.email}
            readOnly
            disabled
            hint="Your sign-in identity. Ask an admin to re-invite you if it needs to change."
          />
          {mutation.error ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <p className="text-[12px] text-kw-danger-text">
                {mutation.error instanceof Error ? mutation.error.message : 'Could not save your profile'}
              </p>
            </div>
          ) : null}
          <div className="flex justify-end">
            <Button type="submit" isLoading={mutation.isPending} disabled={!valid || !dirty}>
              Save changes
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-6">
        <h2 className="text-[15px] font-medium text-kw-shark">Organizations</h2>
        <p className="mt-1 text-[13px] text-kw-muted">
          Memberships attached to your account. Use the organization switcher in the header to change which
          one you are working in.
        </p>
        <ul className="mt-4 divide-y divide-kw-border">
          {memberships.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <Building2 className="size-4 shrink-0 text-kw-muted" />
                <span className="truncate text-[14px] text-kw-shark">{m.organization.name}</span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {m.organizationId === me.organization.id ? <Badge variant="info">Current</Badge> : null}
                <Badge className="capitalize">{m.role.replace('_', ' ')}</Badge>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
